import React from "react";

// ─────────────────────────────────────────────────────────────────
// STYLE SYSTEM
// ─────────────────────────────────────────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Definition / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Bracket({ x, y, h, side, color='#1F3864', w=10 }) {
  if (side === 'left')  return <path d={`M ${x+w},${y} L ${x},${y} L ${x},${y+h} L ${x+w},${y+h}`} fill="none" stroke={color} strokeWidth="2"/>;
  if (side === 'right') return <path d={`M ${x-w},${y} L ${x},${y} L ${x},${y+h} L ${x-w},${y+h}`} fill="none" stroke={color} strokeWidth="2"/>;
  return null;
}

function Arr({ id, color }) {
  return (
    <marker id={id} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
      <polygon points="0 0,7 3.5,0 7" fill={color}/>
    </marker>
  );
}

function Vec({ x1, y1, x2, y2, color, width=2.5, dash='', mid }) {
  return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={width}
    strokeDasharray={dash} markerEnd={`url(#${mid})`}/>;
}

// Simple grid helper
function Grid({ ox, oy, sc, x0, x1, y0, y1 }) {
  const cols = [], rows = [];
  for (let v = Math.ceil(x0); v <= Math.floor(x1); v++) cols.push(v);
  for (let v = Math.ceil(y0); v <= Math.floor(y1); v++) rows.push(v);
  return (
    <g>
      {cols.map(v => <line key={`c${v}`} x1={ox+v*sc} y1={oy-y1*sc} x2={ox+v*sc} y2={oy-y0*sc} stroke="#f0f0f0" strokeWidth="1"/>)}
      {rows.map(v => <line key={`r${v}`} x1={ox+x0*sc} y1={oy-v*sc} x2={ox+x1*sc} y2={oy-v*sc} stroke="#f0f0f0" strokeWidth="1"/>)}
      <line x1={ox+x0*sc} y1={oy} x2={ox+x1*sc} y2={oy} stroke="#ccc" strokeWidth="1.3"/>
      <line x1={ox} y1={oy-y1*sc} x2={ox} y2={oy-y0*sc} stroke="#ccc" strokeWidth="1.3"/>
    </g>
  );
}

// Matrix cell renderer
function Mat({ data, x0, y0, cW=52, cH=38, colors=null, color='#1F3864' }) {
  const R = data.length, C = data[0].length;
  return (
    <g>
      <Bracket x={x0-5} y={y0-4} h={R*cH+8} side="left"  color={color}/>
      <Bracket x={x0+C*cW+5} y={y0-4} h={R*cH+8} side="right" color={color}/>
      {data.map((row, r) => row.map((val, c) => {
        const bg = colors?.[r]?.[c] ?? null;
        return (
          <g key={`${r}${c}`}>
            {bg && <rect x={x0+c*cW+2} y={y0+r*cH+2} width={cW-4} height={cH-4} rx={3} fill={bg} opacity="0.65"/>}
            <text x={x0+c*cW+cW/2} y={y0+r*cH+cH/2+5}
              textAnchor="middle" fontSize="13" fill="#1F3864"
              fontFamily="'Courier New',monospace" fontWeight={bg?'700':'400'}>{val}</text>
          </g>
        );
      }))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Geometric meaning: eigenvectors don't rotate
// Left: random vector v rotates under A.  Right: eigenvector only stretches.
// ─────────────────────────────────────────────────────────────────
function DiagramGeometric() {
  const W = 880, H = 310;
  const sc = 54;

  // A = [[3,1],[0,2]]  eigenvectors: v1=(1,0) λ=3, v2=(1,1) λ=2
  // A random vector w=(0.6,1) → Aw=(2.8,2) — rotates
  // Eigenvector v1=(1,0) → Av1=(3,0) — just stretches ×3
  // Eigenvector v2=(1,1) → Av2=(4,2) = 2*(2,1)... let's use normalised
  // Actually Av2=(3+1,0+2)=(4,2) ≠ 2*(1,1)=(2,2). Let me pick cleaner example.
  // A=[[2,1],[0,3]]: e-vals 2,3. v1=(1,0)→(2,0)=2*v1 ✓, v2 for λ=3: (A-3I)v=0 → [[-1,1],[0,0]] → v=(1,1)
  // A*(1,1)=(3,3)=3*(1,1) ✓

  const applyA = ([x, y]) => [2*x + 1*y, 0*x + 3*y];

  // Random vector (0.4, 1)
  const w    = [0.4, 1.0];
  const Aw   = applyA(w);   // (1.8, 3.0)
  // Eigenvector 1: (1,0), λ=2
  const v1   = [1.0, 0.0];
  const Av1  = applyA(v1);  // (2,0) = 2*(1,0) ✓
  // Eigenvector 2: (1,1)/√2 normalised, λ=3
  const v2n  = [0.707, 0.707];
  const Av2n = [applyA([1,1])[0]*0.707, applyA([1,1])[1]*0.707]; // (2.12,2.12) = 3*v2n ✓

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Geometric Meaning — Eigenvectors Are the Directions That Only Stretch, Never Rotate</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="g_w"   color="#888"/>
          <Arr id="g_Aw"  color="#c0392b"/>
          <Arr id="g_v1"  color="#2E75B6"/>
          <Arr id="g_Av1" color="#2E75B6"/>
          <Arr id="g_v2"  color="#27ae60"/>
          <Arr id="g_Av2" color="#27ae60"/>
          <Arr id="g_arc" color="#c0392b"/>
        </defs>

        {/* ─── LEFT panel: non-eigenvector rotates ─── */}
        <rect x={10} y={8} width={410} height={290} rx={6} fill="#fff" stroke="#c0392b" strokeWidth="1.6"/>
        <text x={215} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Ordinary Vector — Rotates AND Scales
        </text>
        <text x={215} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          Av points in a completely different direction than v
        </text>
        <Grid ox={130} oy={195} sc={sc} x0={-1} x1={2.5} y0={-0.5} y1={3.5}/>

        {/* w before */}
        <Vec x1={130} y1={195} x2={130+w[0]*sc-2}  y2={195-w[1]*sc}  color="#888"    mid="g_w"/>
        <text x={130+w[0]*sc+5} y={195-w[1]*sc+4} fontSize="13" fill="#888" fontWeight="700">v</text>

        {/* Aw after */}
        <Vec x1={130} y1={195} x2={130+Aw[0]*sc-3} y2={195-Aw[1]*sc} color="#c0392b" mid="g_Aw"/>
        <text x={130+Aw[0]*sc+5} y={195-Aw[1]*sc+4} fontSize="13" fill="#c0392b" fontWeight="700">Av</text>

        {/* arc showing angle change */}
        {(() => {
          const r = 38;
          const a1 = Math.atan2(-w[1],  w[0]);
          const a2 = Math.atan2(-Aw[1], Aw[0]);
          const x1s = 130 + r*Math.cos(a1), y1s = 195 + r*Math.sin(a1);
          const x2s = 130 + r*Math.cos(a2), y2s = 195 + r*Math.sin(a2);
          return (
            <g>
              <path d={`M ${x1s},${y1s} A ${r} ${r} 0 0 0 ${x2s},${y2s}`}
                fill="none" stroke="#c0392b" strokeWidth="1.5" strokeDasharray="3,2"/>
              <text x={130+r+12} y={195-r+8} fontSize="12" fill="#c0392b" fontWeight="700">θ ≠ 0</text>
              <text x={130+r+12} y={195-r+24} fontSize="11" fill="#c0392b">direction changes</text>
            </g>
          );
        })()}

        <rect x={18} y={260} width={394} height={28} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={215} y={278} textAnchor="middle" fontSize="11.5" fill="#c0392b" fontWeight="700">
          Av is NOT parallel to v — this is not an eigenvector
        </text>

        {/* ─── RIGHT panel: eigenvectors only stretch ─── */}
        <rect x={450} y={8} width={420} height={290} rx={6} fill="#fff" stroke="#27ae60" strokeWidth="1.6"/>
        <text x={660} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Eigenvectors — Only Scale, Never Rotate
        </text>
        <text x={660} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          Av₁ = 2v₁   and   Av₂ = 3v₂  — parallel, just longer
        </text>
        <Grid ox={580} oy={195} sc={sc} x0={-0.5} x1={3} y0={-0.5} y1={3.5}/>

        {/* v1=(1,0), Av1=(2,0) */}
        <Vec x1={580} y1={195} x2={580+v1[0]*sc-3}   y2={195-v1[1]*sc}   color="#2E75B6" width={2}   mid="g_v1"  dash="5,3"/>
        <Vec x1={580} y1={195} x2={580+Av1[0]*sc-3}  y2={195-Av1[1]*sc}  color="#2E75B6" width={3}   mid="g_Av1"/>
        <text x={580+v1[0]*sc+4}  y={195+14} fontSize="12" fill="#2E75B6">v₁</text>
        <text x={580+Av1[0]*sc+5} y={195-10} fontSize="13" fill="#2E75B6" fontWeight="700">Av₁=2v₁</text>

        {/* v2=(1,1)/√2 normalised, Av2=3*v2n */}
        <Vec x1={580} y1={195} x2={580+v2n[0]*sc-2}   y2={195-v2n[1]*sc}   color="#27ae60" width={2}   mid="g_v2"  dash="5,3"/>
        <Vec x1={580} y1={195} x2={580+Av2n[0]*sc-2}  y2={195-Av2n[1]*sc}  color="#27ae60" width={3}   mid="g_Av2"/>
        <text x={580+v2n[0]*sc+5}  y={195-v2n[1]*sc+4}  fontSize="12" fill="#27ae60">v₂</text>
        <text x={580+Av2n[0]*sc+5} y={195-Av2n[1]*sc+4} fontSize="13" fill="#27ae60" fontWeight="700">Av₂=3v₂</text>

        {/* λ labels */}
        <rect x={458} y={210} width={130} height={44} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={523} y={227} textAnchor="middle" fontSize="11.5" fill="#2E75B6" fontWeight="700">λ₁ = 2</text>
        <text x={523} y={245} textAnchor="middle" fontSize="11" fill="#555">scales by 2 along x</text>

        <rect x={596} y={210} width={130} height={44} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={661} y={227} textAnchor="middle" fontSize="11.5" fill="#27ae60" fontWeight="700">λ₂ = 3</text>
        <text x={661} y={245} textAnchor="middle" fontSize="11" fill="#555">scales by 3 along diagonal</text>

        <rect x={458} y={260} width={404} height={28} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={660} y={278} textAnchor="middle" fontSize="11.5" fill="#27ae60" fontWeight="700">
          Av = λv — same direction, magnitude multiplied by λ
        </text>
      </svg>
      <div style={S.dCaption}>
        Every matrix has special directions — eigenvectors — where the transformation reduces to pure scaling. A random vector gets rotated and scaled; an eigenvector only gets scaled. The scale factor is the eigenvalue λ. Negative λ means the vector flips; |λ|&gt;1 stretches; |λ|&lt;1 shrinks; λ=0 collapses to zero.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Full worked example: finding eigenvalues + eigenvectors
// Step-by-step for A = [[2,1],[0,3]]
// ─────────────────────────────────────────────────────────────────
function DiagramWorkedExample() {
  const W = 880, H = 350;

  const steps = [
    {
      title: 'Step 1 — Form A − λI',
      color: '#2E75B6',
      bg:    '#d0e8ff',
      lines: [
        'A = [[2, 1], [0, 3]]',
        '',
        'A − λI =',
        '[[2−λ,  1 ],',
        ' [ 0,  3−λ]]',
      ],
    },
    {
      title: 'Step 2 — Characteristic Polynomial',
      color: '#9b59b6',
      bg:    '#f0e0ff',
      lines: [
        'det(A − λI) = 0',
        '',
        '(2−λ)(3−λ) − (1)(0) = 0',
        '',
        'λ² − 5λ + 6 = 0',
        '',
        '(λ−2)(λ−3) = 0',
      ],
    },
    {
      title: 'Step 3 — Eigenvalues',
      color: '#27ae60',
      bg:    '#d4f0d4',
      lines: [
        'λ₁ = 2',
        'λ₂ = 3',
        '',
        'Trace check:',
        'λ₁+λ₂ = 5 = tr(A) ✓',
        '',
        'Det check:',
        'λ₁·λ₂ = 6 = det(A) ✓',
      ],
    },
    {
      title: 'Step 4 — Eigenvector for λ₁=2',
      color: '#2E75B6',
      bg:    '#d0e8ff',
      lines: [
        '(A − 2I)v = 0',
        '',
        '[[0, 1],',
        ' [0, 1]] v = 0',
        '',
        '→  v₂ = 0,  v₁ free',
        '',
        'v₁ = [1, 0]',
      ],
    },
    {
      title: 'Step 5 — Eigenvector for λ₂=3',
      color: '#27ae60',
      bg:    '#d4f0d4',
      lines: [
        '(A − 3I)v = 0',
        '',
        '[[-1, 1],',
        ' [ 0, 0]] v = 0',
        '',
        '→  −v₁+v₂=0 → v₁=v₂',
        '',
        'v₂ = [1, 1]',
      ],
    },
  ];

  const pw = 162;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Full Worked Example — Finding Eigenvalues and Eigenvectors of a 2×2 Matrix</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {steps.map((st, si) => {
          const px = si * (pw + 8) + 4;
          return (
            <g key={si}>
              {/* Panel */}
              <rect x={px} y={8} width={pw} height={330} rx={5}
                fill="#fff" stroke={st.color} strokeWidth="1.8"/>

              {/* Header */}
              <rect x={px} y={8} width={pw} height={34} rx={5} fill={st.bg}/>
              <rect x={px} y={30} width={pw} height={12} fill={st.bg}/>
              <text x={px+pw/2} y={29} textAnchor="middle" fontSize="11"
                fill={st.color} fontWeight="700">{st.title}</text>

              {/* Lines */}
              {st.lines.map((line, li) => (
                <text key={li} x={px+10} y={58+li*20}
                  fontSize="12" fill={line.startsWith('λ') || line.startsWith('v') ? st.color : '#1F3864'}
                  fontFamily="'Courier New',monospace"
                  fontWeight={line.includes('✓') || line.startsWith('λ₁ =') || line.startsWith('λ₂ =') ? '700' : '400'}>
                  {line}
                </text>
              ))}

              {/* Arrow to next */}
              {si < steps.length - 1 && (
                <text x={px+pw+2} y={178} fontSize="18" fill="#bbb">→</text>
              )}
            </g>
          );
        })}

        {/* Bottom verification row */}
        <rect x={4} y={298} width={872} height={44} rx={5} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={440} y={315} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Verification:  Av₁ = [[2,1],[0,3]]·[1,0] = [2,0] = 2·[1,0] ✓       Av₂ = [[2,1],[0,3]]·[1,1] = [3,3] = 3·[1,1] ✓
        </text>
        <text x={440} y={333} textAnchor="middle" fontSize="11" fill="#555">
          Always verify by computing Av and checking it equals λv — catches arithmetic errors immediately
        </text>
      </svg>
      <div style={S.dCaption}>
        The five steps for any eigenvalue problem: form A−λI, set its determinant to zero, solve the characteristic polynomial for eigenvalues, then for each eigenvalue solve (A−λI)v=0 for the eigenvector. The trace and determinant checks in step 3 are fast sanity checks — eigenvalues must sum to the trace and multiply to the determinant.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Diagonalization A = PDP⁻¹
// Show the matrix, its eigenvector matrix P, diagonal D, and P⁻¹
// Then show what Aⁿ = PDⁿP⁻¹ means
// ─────────────────────────────────────────────────────────────────
function DiagramDiagonalization() {
  const W = 880, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Diagonalization — A = PDP⁻¹ and Why It Matters</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ─── TOP ROW: A = PDP⁻¹ decomposition ─── */}
        {/* A */}
        <text x={52} y={28} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">A</text>
        <Mat data={[['2','1'],['0','3']]} x0={10} y0={38} cW={48} cH={40}/>
        <text x={120} y={70} fontSize="24" fill="#888">=</text>

        {/* P — columns are eigenvectors */}
        <text x={196} y={28} textAnchor="middle" fontSize="14" fill="#2E75B6" fontWeight="700">P</text>
        <text x={196} y={42} textAnchor="middle" fontSize="10.5" fill="#2E75B6">(eigenvectors)</text>
        <Mat data={[['1','1'],['0','1']]} x0={148} y0={50} cW={48} cH={40}
          colors={[[null,'#d0e8ff'],[null,'#d4f0d4']]} color="#2E75B6"/>
        <text x={258} y={70} fontSize="24" fill="#888">·</text>

        {/* D — diagonal eigenvalues */}
        <text x={334} y={28} textAnchor="middle" fontSize="14" fill="#9b59b6" fontWeight="700">D</text>
        <text x={334} y={42} textAnchor="middle" fontSize="10.5" fill="#9b59b6">(eigenvalues)</text>
        <Mat data={[['2','0'],['0','3']]} x0={286} y0={50} cW={48} cH={40}
          colors={[['#f0e0ff',null],[null,'#f0e0ff']]} color="#9b59b6"/>
        <text x={396} y={70} fontSize="24" fill="#888">·</text>

        {/* P⁻¹ */}
        <text x={472} y={28} textAnchor="middle" fontSize="14" fill="#c0392b" fontWeight="700">P⁻¹</text>
        <Mat data={[['1','-1'],['0','1']]} x0={424} y0={50} cW={52} cH={40} color="#c0392b"/>

        {/* Legend */}
        <rect x={548} y={16} width={322} height={102} rx={5} fill="#f8fbff" stroke="#ccd9ea" strokeWidth="1"/>
        <text x={709} y={34} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Reading the decomposition</text>
        <rect x={558} y={42} width={12} height={12} rx={2} fill="#d0e8ff" stroke="#2E75B6" strokeWidth="1"/>
        <text x={576} y={53} fontSize="11.5" fill="#2E75B6">P col 1 = v₁ = (1,0) for λ₁=2</text>
        <rect x={558} y={60} width={12} height={12} rx={2} fill="#d4f0d4" stroke="#27ae60" strokeWidth="1"/>
        <text x={576} y={71} fontSize="11.5" fill="#27ae60">P col 2 = v₂ = (1,1) for λ₂=3</text>
        <rect x={558} y={78} width={12} height={12} rx={2} fill="#f0e0ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={576} y={89} fontSize="11.5" fill="#9b59b6">D diagonal = eigenvalues λ₁, λ₂</text>
        <text x={558} y={109} fontSize="11" fill="#888">P⁻¹ converts back to standard coords</text>

        {/* Divider */}
        <line x1={10} y1={140} x2={870} y2={140} stroke="#e0e8f0" strokeWidth="1.5"/>

        {/* ─── BOTTOM: Powers via diagonalization ─── */}
        <text x={440} y={162} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Why Diagonalization Is Powerful — Computing Aⁿ
        </text>

        {/* Four panels side by side */}
        {[
          { label:'A¹', vals:[['2','1'],['0','3']], note:'n=1', color:'#2E75B6' },
          { label:'A²', vals:[['4','5'],['0','9']], note:'n=2', color:'#9b59b6' },
          { label:'A³', vals:[['8','19'],['0','27']], note:'n=3', color:'#27ae60' },
          { label:'Aⁿ', vals:[['2ⁿ','3ⁿ−2ⁿ'],['0','3ⁿ']], note:'general', color:'#e07b00' },
        ].map((item, i) => {
          const px = 10 + i * 215;
          return (
            <g key={i}>
              <rect x={px} y={172} width={200} height={130} rx={5}
                fill="#fff" stroke={item.color} strokeWidth={i===3 ? 2.2 : 1.4}/>
              <text x={px+100} y={190} textAnchor="middle" fontSize="13"
                fill={item.color} fontWeight="700">{item.label}</text>
              <text x={px+100} y={204} textAnchor="middle" fontSize="10.5"
                fill="#888">{item.note}</text>
              <Mat data={item.vals} x0={px+26} y0={212} cW={70} cH={40} color={item.color}
                colors={i===3 ? [[item.color+'22',null],[null,item.color+'22']] : null}/>
            </g>
          );
        })}

        <rect x={10} y={278} width={860} height={32} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={440} y={293} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Aⁿ = PDⁿP⁻¹  — raising D to the nth power is trivial: just raise each diagonal entry
        </text>
        <text x={440} y={308} textAnchor="middle" fontSize="11" fill="#555">
          Without diagonalization, computing A¹⁰⁰ requires 99 matrix multiplications. With it: just 2ⁿ and 3ⁿ on the diagonal.
        </text>
      </svg>
      <div style={S.dCaption}>
        Diagonalization expresses A in a basis where the transformation is just scaling — the eigenvector basis. P changes to that basis, D scales each axis by its eigenvalue, P⁻¹ changes back. The payoff: Aⁿ = PDⁿP⁻¹ reduces matrix powers to scalar powers of the eigenvalues.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Symmetric matrices: spectral theorem + orthogonal eigenvectors
// ─────────────────────────────────────────────────────────────────
function DiagramSymmetric() {
  const W = 880, H = 300;
  const sc = 52;

  // Symmetric A = [[3,1],[1,3]]
  // Eigenvalues: λ=(3+1)=4, λ=(3-1)=2
  // Eigenvectors: (1,1)/√2 for λ=4, (1,-1)/√2 for λ=2  — ORTHOGONAL

  const sq2 = Math.SQRT2;
  const v1 = [1/sq2, 1/sq2];   // λ=4
  const v2 = [1/sq2, -1/sq2];  // λ=2
  const Av1 = [3/sq2+1/sq2, 1/sq2+3/sq2]; // = (4/sq2, 4/sq2) = 4*v1 ✓
  const Av2 = [3/sq2-1/sq2, 1/sq2-3/sq2]; // = (2/sq2,-2/sq2) = 2*v2 ✓

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Symmetric Matrices — Real Eigenvalues and Orthogonal Eigenvectors (Spectral Theorem)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="sym_v1"  color="#2E75B6"/>
          <Arr id="sym_v2"  color="#c0392b"/>
          <Arr id="sym_Av1" color="#2E75B6"/>
          <Arr id="sym_Av2" color="#c0392b"/>
        </defs>

        {/* ─── LEFT: eigenvalue/eigenvector diagram ─── */}
        <rect x={10} y={8} width={390} height={282} rx={6} fill="#fff" stroke="#2E75B6" strokeWidth="1.6"/>
        <text x={205} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Symmetric A = [[3,1],[1,3]]
        </text>
        <text x={205} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          Eigenvectors are perpendicular — guaranteed by symmetry
        </text>
        <Grid ox={175} oy={185} sc={sc} x0={-1.5} x1={1.8} y0={-1.8} y1={1.8}/>

        {/* right-angle mark between eigenvectors */}
        <rect x={175+7} y={185-14} width={14} height={14} fill="none" stroke="#888" strokeWidth="1.2"/>

        {/* v1 */}
        <Vec x1={175} y1={185} x2={175+v1[0]*sc-2} y2={185-v1[1]*sc} color="#2E75B6" width={2.5} mid="sym_v1"/>
        <text x={175+v1[0]*sc+5} y={185-v1[1]*sc+4} fontSize="13" fill="#2E75B6" fontWeight="700">v₁  λ=4</text>
        {/* Av1 scaled down for display */}
        <Vec x1={175} y1={185} x2={175+Av1[0]*sc*0.6-2} y2={185-Av1[1]*sc*0.6} color="#2E75B6" width={3} mid="sym_Av1" dash="5,3"/>
        <text x={175+Av1[0]*sc*0.6+5} y={185-Av1[1]*sc*0.6} fontSize="12" fill="#2E75B6">4v₁</text>

        {/* v2 */}
        <Vec x1={175} y1={185} x2={175+v2[0]*sc-2} y2={185-v2[1]*sc} color="#c0392b" width={2.5} mid="sym_v2"/>
        <text x={175+v2[0]*sc+5} y={185-v2[1]*sc+5} fontSize="13" fill="#c0392b" fontWeight="700">v₂  λ=2</text>
        {/* Av2 */}
        <Vec x1={175} y1={185} x2={175+Av2[0]*sc*0.75-2} y2={185-Av2[1]*sc*0.75} color="#c0392b" width={3} mid="sym_Av2" dash="5,3"/>
        <text x={175+Av2[0]*sc*0.75+5} y={185-Av2[1]*sc*0.75+5} fontSize="12" fill="#c0392b">2v₂</text>

        <text x={30} y={135} fontSize="11.5" fill="#888" fontStyle="italic">v₁·v₂ = 0</text>
        <text x={30} y={150} fontSize="11.5" fill="#888" fontStyle="italic">orthogonal</text>

        <rect x={18} y={252} width={364} height={28} rx={3} fill="#EBF3FB"/>
        <text x={200} y={270} textAnchor="middle" fontSize="11.5" fill="#2E75B6" fontWeight="700">
          Spectral Theorem: A = QΛQᵀ  (Q orthogonal, Λ diagonal)
        </text>

        {/* ─── RIGHT: properties table ─── */}
        <rect x={416} y={8} width={454} height={282} rx={6} fill="#fff" stroke="#27ae60" strokeWidth="1.6"/>
        <text x={643} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Why Symmetric Matrices Are Special
        </text>

        {[
          ['Real eigenvalues',    '#27ae60', 'All λ are real — no complex numbers\nTrue even if A has complex-looking entries'],
          ['Orthogonal eigenvecs','#2E75B6', 'Eigenvectors for different λ are perpendicular\nv₁·v₂ = 0 — always, guaranteed'],
          ['Orthogonal P',        '#9b59b6', 'P is an orthogonal matrix: Pᵀ = P⁻¹\nA = QΛQᵀ — very fast to invert'],
          ['Positive definite',   '#e07b00', 'If all λ > 0: xᵀAx > 0 for all x ≠ 0\nStiffness matrices, covariance matrices are PD'],
          ['Positive semidefinite','#c0392b','If all λ ≥ 0: xᵀAx ≥ 0\nSingular but all eigenvalues non-negative'],
        ].map(([label, color, note], i) => (
          <g key={i}>
            <rect x={424} y={46+i*44} width={438} height={38} rx={4}
              fill={i%2===0 ? '#f8fbff' : '#fff'} stroke="#eee" strokeWidth="1"/>
            <text x={434} y={63+i*44} fontSize="12" fill={color} fontWeight="700">{label}</text>
            {note.split('\n').map((line, li) => (
              <text key={li} x={434} y={76+i*44+li*13} fontSize="10.5" fill="#555"
                fontFamily="'Courier New',monospace">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Symmetric matrices are the most important class in engineering — stiffness, mass, stress, and covariance matrices are all symmetric. The spectral theorem guarantees real eigenvalues and perpendicular eigenvectors, which means the eigenvector basis is not just convenient but physically meaningful: the eigenvectors are the principal axes of whatever physical quantity A represents.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Engineering applications: four panels
// Vibration mode shapes, PCA/principal axes, stability, stress tensor
// ─────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 880, H = 310;

  const apps = [
    {
      title: 'Structural Vibrations',
      color: '#2E75B6', bg: '#d0e8ff',
      eq:    '[K]{x} = ω²[M]{x}',
      eigen: 'ω² = eigenvalues',
      evec:  'mode shapes = eigenvectors',
      note:  'Each eigenvalue = natural frequency squared.\nEigenvectors show which nodes move together.',
      x: 10,
    },
    {
      title: 'Principal Stress',
      color: '#c0392b', bg: '#ffd0d0',
      eq:    '[σ]{n} = σₚ{n}',
      eigen: 'σₚ = principal stresses',
      evec:  'principal directions = eigenvectors',
      note:  'Stress tensor eigenvalues are max/min normal stress.\nEigenvectors = planes of zero shear stress.',
      x: 230,
    },
    {
      title: 'PCA / Principal Axes',
      color: '#9b59b6', bg: '#f0e0ff',
      eq:    '[C]{v} = λ{v}',
      eigen: 'λ = variance along axis',
      evec:  'principal components = eigenvectors',
      note:  'Covariance matrix eigenvalues = spread of data.\nEigenvectors = uncorrelated directions (axes of ellipse).',
      x: 450,
    },
    {
      title: 'System Stability',
      color: '#27ae60', bg: '#d4f0d4',
      eq:    'ẋ = Ax  (state-space)',
      eigen: 'λ = eigenvalues of A',
      evec:  'modes = eigenvectors of A',
      note:  'System stable iff all Re(λ) < 0.\nEach eigenvalue governs one mode of decay or growth.',
      x: 670,
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Engineering Applications — Where Eigenvalues Appear in Practice</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {apps.map((ap, i) => {
          const pw = 204, ph = 290;
          return (
            <g key={i}>
              <rect x={ap.x} y={8} width={pw} height={ph} rx={6}
                fill="#fff" stroke={ap.color} strokeWidth="1.8"/>

              {/* Header */}
              <rect x={ap.x} y={8} width={pw} height={32} rx={6} fill={ap.bg}/>
              <rect x={ap.x} y={28} width={pw} height={12} fill={ap.bg}/>
              <text x={ap.x+pw/2} y={28} textAnchor="middle" fontSize="12.5"
                fill={ap.color} fontWeight="700">{ap.title}</text>

              {/* Equation */}
              <rect x={ap.x+8} y={48} width={pw-16} height={28} rx={3} fill={ap.bg} opacity="0.5"/>
              <text x={ap.x+pw/2} y={67} textAnchor="middle" fontSize="12"
                fill={ap.color} fontFamily="'Courier New',monospace" fontWeight="700">{ap.eq}</text>

              {/* Eigenvalue label */}
              <text x={ap.x+10} y={96} fontSize="11" fill="#1F3864" fontWeight="700">Eigenvalues:</text>
              <text x={ap.x+10} y={111} fontSize="11.5" fill={ap.color}
                fontFamily="'Courier New',monospace">{ap.eigen}</text>

              {/* Eigenvector label */}
              <text x={ap.x+10} y={134} fontSize="11" fill="#1F3864" fontWeight="700">Eigenvectors:</text>
              <text x={ap.x+10} y={149} fontSize="11" fill={ap.color}>{ap.evec}</text>

              {/* Divider */}
              <line x1={ap.x+8} y1={162} x2={ap.x+pw-8} y2={162} stroke="#eee" strokeWidth="1"/>

              {/* Note */}
              {ap.note.split('\n').map((line, li) => (
                <text key={li} x={ap.x+10} y={178+li*16} fontSize="10.5" fill="#555">{line}</text>
              ))}

              {/* Mini visual */}
              {i === 0 && (
                // Two mode shape dots
                <g>
                  {[[70,230],[105,218],[140,230],[175,242]].map(([x,y],j) => (
                    <g key={j}>
                      <line x1={x} y1={255} x2={x} y2={y} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="3,2"/>
                      <circle cx={x} cy={y} r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.2"/>
                    </g>
                  ))}
                  <line x1={60} y1={256} x2={185} y2={256} stroke="#2E75B6" strokeWidth="1.5"/>
                  <text x={ap.x+pw/2} y={278} textAnchor="middle" fontSize="10" fill="#2E75B6">mode shape (eigenvector)</text>
                </g>
              )}
              {i === 1 && (
                // Stress ellipse
                <g>
                  <ellipse cx={330} cy={248} rx={35} ry={20} fill="none" stroke="#c0392b" strokeWidth="1.5" transform="rotate(-30,330,248)"/>
                  <line x1={330} y1={248} x2={330+35*Math.cos(-0.52)} y2={248+35*Math.sin(-0.52)}
                    stroke="#c0392b" strokeWidth="2" markerEnd="url(#app_s1)"/>
                  <line x1={330} y1={248} x2={330+20*Math.cos(1.05)} y2={248+20*Math.sin(1.05)}
                    stroke="#c0392b" strokeWidth="2" markerEnd="url(#app_s2)"/>
                  <defs>
                    <Arr id="app_s1" color="#c0392b"/>
                    <Arr id="app_s2" color="#c0392b"/>
                  </defs>
                  <text x={330} y={278} textAnchor="middle" fontSize="10" fill="#c0392b">σ₁, σ₂ axes</text>
                </g>
              )}
              {i === 2 && (
                // Data ellipse with PCA axes
                <g>
                  {[[545,245],[558,238],[572,232],[580,238],[595,248],[610,254],[620,248]].map(([x,y],j) => (
                    <circle key={j} cx={x} cy={y} r={3} fill="#9b59b6" opacity="0.6"/>
                  ))}
                  <ellipse cx={582} cy={244} rx={38} ry={14} fill="none" stroke="#9b59b6" strokeWidth="1.5" transform="rotate(-20,582,244)"/>
                  <line x1={555} y1={254} x2={612} y2={233} stroke="#9b59b6" strokeWidth="2"/>
                  <line x1={572} y1={231} x2={592} y2={257} stroke="#9b59b6" strokeWidth="1.5" strokeDasharray="3,2"/>
                  <text x={582} y={278} textAnchor="middle" fontSize="10" fill="#9b59b6">principal components</text>
                </g>
              )}
              {i === 3 && (
                // Stability: decaying spiral
                <g>
                  {Array.from({length:12}, (_,j) => {
                    const t = j * 0.6;
                    const r = 28 * Math.exp(-0.18*t);
                    const x = 775 + r*Math.cos(t);
                    const y = 248 + r*Math.sin(t);
                    return j===0 ? null : (
                      <line key={j}
                        x1={775 + 28*Math.exp(-0.18*(t-0.6))*Math.cos(t-0.6)}
                        y1={248 + 28*Math.exp(-0.18*(t-0.6))*Math.sin(t-0.6)}
                        x2={x} y2={y}
                        stroke="#27ae60" strokeWidth="1.8"/>
                    );
                  })}
                  <circle cx={775} cy={248} r={3} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
                  <text x={775} y={278} textAnchor="middle" fontSize="10" fill="#27ae60">Re(λ)&lt;0 → stable</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Four domains where eigenvalues are the answer: structural vibrations (frequencies), stress analysis (principal stresses and directions), data analysis (variance axes), and control systems (stability). In every case the eigenvalue is a scale factor of physical significance and the eigenvector is the associated physical direction or shape.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function EigenvaluesEigenvectors() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Eigenvalues &amp; Eigenvectors</h1>

      <p style={S.p}>
        The Matrices page introduced the eigenvalue equation Av = λv and showed how to set up the characteristic polynomial. This page develops the full story: what eigenvectors mean geometrically, how to solve a complete worked example from scratch, diagonalization and why it makes matrix powers trivial, the special properties of symmetric matrices, and the depth of engineering applications — from vibration mode shapes to control system stability.
      </p>

      <InfoBox
        title="The Eigenvalue Equation:  Av = λv  —  v is an eigenvector, λ is its eigenvalue"
        items={[
          "Av = λv means: the transformation A maps v to a scalar multiple of itself",
          "v does not rotate under A — it only stretches (λ>1), shrinks (0<λ<1), flips (λ<0), or collapses (λ=0)",
          "Every square matrix has exactly n eigenvalues (counting multiplicity, possibly complex)",
          "Eigenvectors are not unique — any non-zero scalar multiple cv is also an eigenvector for the same λ",
        ]}
      />

      <h2 style={S.h2}>Geometric Meaning</h2>
      <DiagramGeometric />
      <Table2 rows={[
        ["λ > 1",              "Eigenvector stretches — Av is longer than v in the same direction"],
        ["0 < λ < 1",          "Eigenvector shrinks — Av is shorter than v, same direction"],
        ["λ = 1",              "Eigenvector is fixed — Av = v, the identity direction for this axis"],
        ["λ < 0",              "Eigenvector flips — Av points opposite to v, then scales by |λ|"],
        ["λ = 0",              "Eigenvector collapses — Av = 0, this direction is in the null space\nMatrix is singular: det(A) = product of all eigenvalues = 0"],
        ["Non-eigenvector",    "Rotates AND scales — Av points in a different direction than v\nThe angle between v and Av is non-zero for most vectors"],
      ]} />

      <h2 style={S.h2}>Finding Eigenvalues and Eigenvectors — Full Worked Example</h2>
      <DiagramWorkedExample />
      <Table2 rows={[
        ["Step 1 — Form A−λI",      "Subtract λ from every diagonal entry\nOff-diagonal entries stay unchanged"],
        ["Step 2 — Characteristic polynomial", "det(A−λI) = 0 — an nth degree polynomial in λ\n2×2: quadratic.  3×3: cubic.  Solved analytically for small n, numerically for large."],
        ["Step 3 — Eigenvalues",    "Roots of the characteristic polynomial\nSum of eigenvalues = trace(A).  Product of eigenvalues = det(A) — use as sanity checks"],
        ["Step 4 — Eigenvectors",   "For each λ: solve (A−λI)v = 0 via row reduction\nResult is always underdetermined — one free variable per eigenvector"],
        ["Normalisation",           "Divide eigenvector by its magnitude to get a unit vector\nNot required but often done for cleanliness and orthogonality checks"],
        ["Repeated eigenvalues",    "Algebraic multiplicity: how many times λ appears as a root\nGeometric multiplicity: dimension of eigenspace ker(A−λI)\nGeom ≤ Algeb always — if equal: matrix is diagonalisable"],
      ]} />

      <h2 style={S.h2}>Diagonalization — A = PDP⁻¹</h2>
      <DiagramDiagonalization />
      <Table2 rows={[
        ["Diagonalizable matrix",    "A is diagonalizable if it has n linearly independent eigenvectors\nEquivalent: geometric multiplicity = algebraic multiplicity for all λ"],
        ["Building P",               "Columns of P = eigenvectors, in order matching eigenvalues in D\nColumn 1 of P corresponds to λ₁ = D[1,1], etc."],
        ["Building D",               "D is diagonal: D[i,i] = λᵢ.  All off-diagonal entries = 0\nOrder of eigenvalues in D must match column order in P"],
        ["A = PDP⁻¹",               "Change to eigenbasis (P⁻¹), scale each axis (D), change back (P)\nSame similarity transform from Linear Transformations page"],
        ["Aⁿ = PDⁿP⁻¹",            "Factor once, then Dⁿ is trivial: raise each diagonal entry to nth power\nUsed in: discrete dynamical systems, Markov chains, coupled ODEs"],
        ["Not all matrices diag.",   "Defective matrix: not enough independent eigenvectors\nUse Jordan normal form instead — beyond this page's scope"],
      ]} />

      <h2 style={S.h2}>Symmetric Matrices and the Spectral Theorem</h2>
      <DiagramSymmetric />
      <Table2 rows={[
        ["Spectral theorem",         "Every real symmetric matrix A has:\n(1) all real eigenvalues\n(2) orthogonal eigenvectors\n(3) A = QΛQᵀ  where Q is orthogonal and Λ diagonal"],
        ["Orthogonal diagonalization","Q⁻¹ = Qᵀ — inverting Q is free (just transpose)\nA = QΛQᵀ is more stable numerically than A = PDP⁻¹"],
        ["Positive definite (PD)",   "All eigenvalues > 0  ↔  xᵀAx > 0 for all x ≠ 0\nStiffness matrices and covariance matrices are always PD"],
        ["Positive semidefinite",    "All eigenvalues ≥ 0  ↔  xᵀAx ≥ 0\nSingular version of PD — occurs in degenerate or constrained systems"],
        ["Indefinite",               "Mix of positive and negative eigenvalues\nSaddle point in optimisation — neither max nor min"],
        ["Engineering relevance",    "FEA stiffness [K] and mass [M] are symmetric PD\nEigensolver output: ω² = eigenvalues, mode shapes = eigenvectors of [K]Φ=[M]Φλ"],
      ]} />

      <h2 style={S.h2}>Engineering Applications in Depth</h2>
      <DiagramApplications />
      <Table2 rows={[
        ["Structural vibrations",    "[K]Φ = ω²[M]Φ — generalised eigenvalue problem\nω² are natural frequencies squared; Φ columns are mode shapes\nFEA modal analysis (ANSYS, Abaqus) solves this for thousands of DOF"],
        ["Buckling analysis",        "[K]Φ = λ[Kσ]Φ — geometric stiffness eigenvalue problem\nSmallest eigenvalue λ = critical load factor for buckling"],
        ["Principal stresses",       "Eigenvalues of 3×3 stress tensor = σ₁, σ₂, σ₃ (principal stresses)\nEigenvectors = principal directions where shear stress = 0\nMohr's circle is a 2D graphical version of this eigenvalue problem"],
        ["PCA / Principal axes",     "Covariance matrix eigenvalues = variance along each principal axis\nUsed in: tolerancing, shape analysis, sensor data compression\nMoment of inertia tensor eigenvalues = principal moments"],
        ["Control system stability", "ẋ = Ax: solution x(t) = Σ cᵢ eλᵢt vᵢ\nSystem stable iff all Re(λᵢ) < 0 — eigenvalues of A must be left-half-plane\nUnstable mode: any λ with Re(λ) > 0 grows exponentially"],
        ["Coupled ODEs",             "Decouple via eigenvectors: change to eigenbasis, each equation independent\nAⁿ = PDⁿP⁻¹ used for discrete-time systems (Markov, digital control)"],
      ]} />

      <InfoBox
        title="Key Takeaway — What to Remember About Every Eigenvalue Problem"
        items={[
          "Eigenvalue = scale factor.  Eigenvector = the direction where only scaling happens, no rotation.",
          "Find λ: solve det(A−λI)=0.  Find v: solve (A−λI)v=0 for each λ.  Verify: compute Av and check it equals λv.",
          "Trace = sum of eigenvalues.  Determinant = product of eigenvalues.  Use both as sanity checks.",
          "Diagonalization A=PDP⁻¹ makes powers trivial: Aⁿ=PDⁿP⁻¹.",
          "Symmetric matrices always have real eigenvalues and orthogonal eigenvectors — they are the physically important class.",
          "In every engineering domain: eigenvalues = natural frequencies, principal stresses, stability margins, or variance axes.",
        ]}
      />
    </div>
  );
}